/* Смена выдачи списка — тот же приём, что у BotCore.results (bot-core.js): запоминаем, где стояли карточки,
   после перестройки замеряем заново; оставшиеся переезжают со старого места на новое, новые проявляются
   по очереди, а ушедшие гаснут копиями в слое .st-ghosts. Координаты копий — от угла этого слоя.
   Старые места снимаются прямо перед тем, как React тронет разметку (getSnapshotBeforeUpdate), как эталон
   меряет их перед paint(): после прокрутки, догрузки или перекладки места те, что на экране сейчас, а не при
   прошлой смене. Всё движение — только transform и opacity; при «уменьшить движение» и заморозке .still переходов нет. */
import { Component, createElement, type ReactElement, type RefObject } from 'react';
import { pop, reduced } from './motion';

const EASE = 'cubic-bezier(.2,.8,.2,1)';
type Places = Map<string, { rect: DOMRect; node: Element }>;
interface Snapshot {
  places: Places;
  /** Разметка подписи над выдачей до смены: сменилась — подпись входит общей анимацией (pop эталона) */
  label: string | null;
}
/** Копия для слоя призраков не должна ловить фокус и повторять идентификаторы живой разметки */
function strip(node: Element) {
  node.removeAttribute('id');
  node.querySelectorAll('[id]').forEach(child => child.removeAttribute('id'));
  node.querySelectorAll('input,button,a,select,textarea,video,audio').forEach(child => {
    child.setAttribute('tabindex', '-1');
    child.setAttribute('aria-hidden', 'true');
  });
  node.setAttribute('aria-hidden', 'true');
}

/** Ключ карточки или строки, как его считает эталон (engine.ts: tileKey, el.dataset.k) */
export function resultKey(node: HTMLElement): string {
  /* Порядок повторяет оба правила эталона одной функцией: у строк ключ всегда в data-k
     (BC.results берёт el.dataset.k), у плиток data-k нет и работает tileKey. */
  const d = node.dataset;
  return d.k ?? (d.app ? 'a' + d.app : d.coll ? 'c' + d.coll : d.tool ? 't' + d.tool : (d.key ?? ''));
}

/** Точка снимка: React вызывает getSnapshotBeforeUpdate до изменений разметки всего коммита, а componentDidUpdate —
    после раскладок, стоящих в дереве раньше (кладка ставит плитки в своём useLayoutEffect), и до отрисовки кадра. */
class Before extends Component<{ signature: string; scope: string; take(): Snapshot | null; play(snapshot: Snapshot): void }> {
  getSnapshotBeforeUpdate(previous: Readonly<{ signature: string; scope: string }>) {
    return previous.signature !== this.props.signature && previous.scope === this.props.scope ? this.props.take() : null;
  }
  componentDidUpdate(_props: unknown, _state: unknown, snapshot: Snapshot | null) {
    if (snapshot) this.props.play(snapshot);
  }
  render() {
    return null;
  }
}

/** Возвращает точку снимка: её ставят в разметке после списка, чтобы замер после смены видел готовую раскладку */
export function useResultsTransition({
  ghosts,
  selector,
  signature,
  scope = '',
  label,
  key: keyOf = resultKey,
}: {
  /** Слой копий: он же задаёт начало координат и колонку, в которой ищутся карточки */
  ghosts: RefObject<HTMLElement | null>;
  /** Как найти карточку: селектор, по которому она ищется в слое выдачи */
  selector: string;
  /** Ключ записи — чтобы узнать ту же карточку на новом месте. По умолчанию правило эталона
      (engine.ts: tileKey и el.dataset.k): data-app → «a123», data-coll → «c…», data-tool → «t…»,
      строки и подзаголовки — data-k как есть. data-key оставлен запасным, пока не все
      экраны перешли на атрибуты эталона. */
  key?: (node: HTMLElement) => string;
  /** Состав выдачи строкой: пока он тот же, перестроения не было и анимировать нечего */
  signature: string;
  /** Область выдачи (вид страницы, режим): при её смене выдача не переезжает — сменился сам экран */
  scope?: string;
  /** Подпись над выдачей (пустая сцена) внутри колонки: сменилась — входит общей анимацией */
  label?: string;
}): ReactElement {
  const measure = (host: Element) => {
    const rects: Places = new Map();
    host.querySelectorAll(selector).forEach(node => {
      const key = keyOf(node as HTMLElement);
      if (key) rects.set(key, { rect: node.getBoundingClientRect(), node });
    });
    return rects;
  };
  const take = (): Snapshot | null => {
    const host = ghosts.current?.parentElement;
    if (!host || !host.animate || reduced()) return null;
    return { places: measure(host), label: label ? (host.querySelector(label)?.innerHTML ?? null) : null };
  };
  const play = ({ places: before, label: labelWas }: Snapshot) => {
    const layer = ghosts.current;
    const host = layer?.parentElement;
    if (!layer || !host) return;
    // все замеры после перестройки — одним проходом до первой анимации, как у эталона
    const after = measure(host);
    const base = layer.getBoundingClientRect();
    const height = innerHeight;
    const offscreen = (rect: DOMRect) => rect.bottom < 0 || rect.top > height;
    let fresh = 0;
    for (const [key, { rect, node }] of after) {
      const old = before.get(key);
      if (!old) {
        // новая карточка: проявляется с небольшой задержкой по очереди, не больше восьми ступеней
        if (!offscreen(rect))
          node.animate([{ opacity: 0, transform: 'translateY(10px) scale(.97)' }, { opacity: 1, transform: 'none' }], {
            duration: 280,
            delay: 70 + Math.min(fresh++, 8) * 28,
            easing: EASE,
            fill: 'backwards',
          });
        continue;
      }
      if (offscreen(rect) && offscreen(old.rect)) continue;
      const dx = old.rect.left - rect.left,
        dy = old.rect.top - rect.top;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5)
        node.animate([{ transform: `translate(${dx}px,${dy}px)` }, { transform: 'none' }], { duration: 360, easing: EASE });
    }
    for (const [key, { rect, node }] of before) {
      // Узел ушедшей карточки React уже снял с разметки — копия снимается и с отсоединённого
      if (after.has(key) || offscreen(rect)) continue;
      const ghost = node.cloneNode(true) as HTMLElement;
      strip(ghost);
      ghost.style.cssText += `;position:absolute;left:${rect.left - base.left}px;top:${rect.top - base.top}px;width:${rect.width}px;height:${rect.height}px;margin:0;pointer-events:none`;
      layer.appendChild(ghost);
      const play = ghost.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.95)' }], {
        duration: 170,
        easing: 'ease-out',
        fill: 'forwards',
      });
      play.onfinish = () => ghost.remove();
      setTimeout(() => ghost.remove(), 500);
    }
    const caption = label ? host.querySelector(label) : null;
    if (caption && caption.innerHTML !== labelWas && caption.firstElementChild) pop(caption);
  };
  return createElement(Before, { signature, scope, take, play });
}
